import type { GetStaticPaths, InferGetStaticPropsType, NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { useApiData } from '@hozana/api/hooks/useApiData'
import { createGetStaticData, createGetStaticProps } from '@hozana/api/server/static'
import { useDispatch } from '@hozana/redux/hooks'
import type { TWrappedServeSideContext } from '@hozana/redux/types'
import { useRouter } from '@hozana/router'
import { LOCAL_STORAGE, localStorage } from '@hozana/storage/browserStorages'
import { TRACKING_SOURCE } from '@hozana/tracking/constants'

import { BorderedDiv } from 'elements/layout/BorderedDiv'
import { Div } from 'elements/layout/Div'
import { Shadow } from 'elements/layout/Shadow'
import { P } from 'elements/text/P'
import { Separator } from 'elements/ui/Separator'

import { Head } from 'general/structure/Head'
import { NAMESPACE } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { IntentionsList } from 'modules/intention/components/IntentionsList'
import { InviteToShareIntentionsMessage } from 'modules/intention/components/InviteToShareIntentionsMessage'
import { UserIntention } from 'modules/intention/components/UserIntention'
import { IntentionsWidgetProvider } from 'modules/intention/providers/IntentionsWidgetProvider'

import { setWidgetIntention } from '../actions'
import { getIntentionQuery, getIntentionsQuery, getWidgetQuery } from '../queries'

const StyledBorderedDiv = styled(BorderedDiv)`
  position: absolute;
  overflow-y: auto;
  box-sizing: border-box;
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
    padding: 5px 0;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.15);
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.08);
    border-left: none;
    border-radius: 10px;
  }
`

const StyledScrollDiv = styled(Div)`
  z-index: ${(props) => props.theme.zIndex.header};
`

export const getStaticPaths: GetStaticPaths<{ guideName: string; subPathParts: string | string[] }> = async () => ({
  paths: [],
  fallback: 'blocking',
})

export const getStaticProps = createGetStaticProps(
  PAGE.INTENTIONS_WIDGET_FORM,
  'common:common.title.default',
  createGetStaticData(({ params }: TWrappedServeSideContext<{ widgetId: string }>) => {
    const widgetId = Number(params.widgetId)
    return {
      widget: {
        query: getWidgetQuery({ widgetId }),
        disabled: !widgetId,
        getStrategy: 'prop', // 'prop' or 'both' needed to get widget lang in creatGetStaticProps
      },
      intentions: {
        query: getIntentionsQuery({ widgetId }),
      },
    }
  }),
  { widget: 'intentions', namespaces: [NAMESPACE.INTENTION] },
)

export const IntentionsWidgetForm: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({ widget }) => {
  const { query } = useRouter()
  const dispatch = useDispatch()

  const widgetId = Number(query.widgetId)

  const [localIntention] = useState(localStorage.getItem(LOCAL_STORAGE.WIDGET_INTENTION(widgetId)))

  useApiData(
    () => ({
      query: getIntentionQuery({
        intentionId: localIntention?.id,
        hash: localIntention?.hash,
      }),
      disabled: !localIntention,
    }),
    [localIntention],
  )

  useEffect(() => {
    if (localIntention) {
      dispatch(setWidgetIntention(localIntention.id, localIntention.hash))
    }
  }, [localIntention, dispatch])

  return (
    <>
      <Head isIndexed={false} />
      <IntentionsWidgetProvider widget={widget}>
        <StyledBorderedDiv h="100%" borderRadius="10px" borderColor="darkBlue" bg="white">
          <StyledScrollDiv bg="darkBlue">
            <Shadow>
              <P color="white" p="5px 1em" bold align="center">
                trans:intention:intentions.subtitle
              </P>
            </Shadow>
          </StyledScrollDiv>
          <UserIntention />
          <InviteToShareIntentionsMessage />
          <Separator />
          <IntentionsList trackingSource={TRACKING_SOURCE.INTENTIONS_WIDGET} />
        </StyledBorderedDiv>
      </IntentionsWidgetProvider>
    </>
  )
}
